import { type ThemeManager } from '../../ThemeManager'
import { SkeletonType } from '../../enums/SkeletonType'
import { RigConfig } from '../../RigConfig'
import { type AnimationWithState } from './interfaces/AnimationWithState'
import { type TransformedAnimationClipPair } from './interfaces/TransformedAnimationClipPair'
import { ClipValidator } from '../../../features/timeline/ClipValidator'

export type AnimationSourceFilter = 'all' | 'default-library' | 'custom-import' | 'user-authored'

export class AnimationSearch extends EventTarget {
  private all_animations: AnimationWithState[] = []
  private readonly filter_input: HTMLInputElement | null = null
  private readonly source_filter_select: HTMLSelectElement | null = null
  private readonly animation_list_container: HTMLElement | null = null
  private filtered_animations_list: AnimationWithState[] = []
  private current_source_filter: AnimationSourceFilter = 'all'

  private readonly theme_manager: ThemeManager
  private readonly skeleton_type: SkeletonType

  private custom_event: CustomEvent | null = null

  constructor (filter_input_id: string, animation_list_container_id: string, theme_manager: ThemeManager, skeleton_type: SkeletonType) {
    super()
    this.filter_input = document.querySelector(`#${filter_input_id}`)
    this.source_filter_select = document.querySelector('#animation-source-filter')
    this.animation_list_container = document.querySelector(`#${animation_list_container_id}`)
    this.theme_manager = theme_manager
    this.skeleton_type = skeleton_type
    this.setup_event_listeners()
  }

  public initialize_animations (animations: TransformedAnimationClipPair[]): void {
    // Convert to animations with state tracking
    this.all_animations = this.map_animations_to_state(animations)

    this.render_filtered_animations('')
  }

  public add_animations (animations: TransformedAnimationClipPair[]): void {
    const new_animations = this.map_animations_to_state(animations)

    this.all_animations.push(...new_animations)
    const filter_text = this.filter_input?.value.toLowerCase() ?? ''
    this.render_filtered_animations(filter_text)
  }

  /**
   * Convert the animation listing data to a format
   * that works for what the UI needs
   * @param animations The list of animations to convert to a format with state for the UI
   * @returns A list of animations with state for the UI to track which animations are selected for export and filtering
   */
  private map_animations_to_state (animations: TransformedAnimationClipPair[]): AnimationWithState[] {
    return animations.map((pair) => {
      const animation_with_state = pair.display_animation_clip as unknown as AnimationWithState
      animation_with_state.isChecked = false
      animation_with_state.metadata = pair.metadata // enhanced searching/display with custom animations
      return animation_with_state
    })
  }

  private setup_event_listeners (): void {
    this.setup_filter_listener()
    this.setup_source_filter_listener()
    this.setup_checkbox_listeners()
    this.setup_theme_change_listener()
  }

  private setup_source_filter_listener (): void {
    if (this.source_filter_select === null) return
    this.source_filter_select.addEventListener('change', (event) => {
      this.current_source_filter = (event.target as HTMLSelectElement).value as AnimationSourceFilter
      this.render_filtered_animations(this.filter_input?.value.toLowerCase() ?? '')
      this.custom_event = new CustomEvent('filtered-animations-listing', { detail: { selectedAnimations: this.get_selected_animation_indices() } })
      this.dispatchEvent(this.custom_event)
    })
  }

  private setup_theme_change_listener (): void {
    // rebuild animation previews so we have the correct theme
    this.theme_manager.addEventListener('theme-changed', () => {
      this.render_filtered_animations(this.filter_input?.value ?? '')
    })
  }

  private setup_filter_listener (): void {
    if (this.filter_input === null) {
      return
    }

    // Add the filter event listener
    this.filter_input.addEventListener('input', (event) => {
      const filter_text = (event.target as HTMLInputElement).value.toLowerCase()
      this.render_filtered_animations(filter_text)

      // emit an event to notify that we have filtered our animation listing
      this.custom_event = new CustomEvent('filtered-animations-listing', { detail: { selectedAnimations: this.get_selected_animation_indices() } })
      this.dispatchEvent(this.custom_event)
    })
  }

  private setup_checkbox_listeners (): void {
    if (this.animation_list_container === null) {
      return
    }

    // Add event listener to the container for checkbox changes (event delegation)
    this.animation_list_container.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      if (target?.type === 'checkbox') {
        this.save_current_checkbox_states()
      }

      // emit an event to notify other parts of the application that export options have changed
      this.custom_event = new CustomEvent('export-options-changed', { detail: { selectedAnimations: this.get_selected_animation_indices() } })
      this.dispatchEvent(this.custom_event)
    })
  }

  private save_current_checkbox_states (): void {
    if (this.animation_list_container === null) {
      return
    }

    const checkboxes = this.animation_list_container.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach((checkbox) => {
      const input = checkbox as HTMLInputElement
      const animation_index = parseInt(input.value)

      if (!isNaN(animation_index) && animation_index < this.all_animations.length) {
        this.all_animations[animation_index].isChecked = input.checked
      }
    })
  }

  /* animations that are shown on UI after filtering */
  public filtered_animations (): AnimationWithState[] {
    return this.filtered_animations_list
  }

  private render_filtered_animations (filter_text: string): void {
    if (this.animation_list_container === null) {
      return
    }

    // Filter animations: name match AND source-type match.
    const source_filter = this.current_source_filter
    this.filtered_animations_list = this.all_animations.filter(animation => {
      if (!animation.name.toLowerCase().includes(filter_text)) return false
      if (source_filter === 'all') return true
      return animation.metadata?.source_type === source_filter
    })

    // Clear and rebuild the animation list
    this.animation_list_container.innerHTML = ''

    // Show "no animations found" if the filtered list is empty
    if (this.filtered_animations_list.length === 0) {
      this.animation_list_container.innerHTML = '<div class="no-animations-message">No animations found</div>'
      return
    }

    this.filtered_animations_list.forEach((animation_clip) => {
      if (this.animation_list_container == null) {
        return
      }

      // Find the original index in the full list for proper data-index
      const original_index = this.all_animations.findIndex(clip => clip === animation_clip)

      // Check if this animation was previously checked
      const was_checked: boolean = animation_clip.isChecked ?? false
      const checked_attribute = was_checked ? 'checked' : ''

      // build out where the video previews will be stored
      // each skeleton type has its own folder
      const preview_folder: string = RigConfig.by_skeleton_type(this.skeleton_type)?.animation_preview_folder ?? 'error'
      if (preview_folder === 'error') {
        console.error('Unknown skeleton type for animation previews. Add the rig to RigConfig.ts.')
      }

      const anim_name: string = animation_clip.name
      const theme_name: string = this.theme_manager.get_current_theme()
      const is_custom_animation = animation_clip.metadata?.source_type === 'custom-import'
      const is_user_authored = animation_clip.metadata?.source_type === 'user-authored'
      const source_skeleton_origin = animation_clip.metadata?.source_skeleton_origin

      const preview_data_src_attribute = (is_custom_animation || is_user_authored)
        ? ''
        : ` data-src="../animpreviews/${preview_folder}/${theme_name}_${anim_name}.webm"`

      // Provenance badge: a single short tag in the top-left of the clip thumb.
      //   "C"        — user-imported custom GLB/FBX
      //   "M" / "Q" / "T" — custom import auto-detected from Mixamo / Quaternius / Tripo
      //   "✏︎"        — clip authored in-app via "+ New Clip" + Pose-and-Bake
      let badge_text = ''
      let badge_label = ''
      if (is_user_authored) {
        badge_text = '<span class="material-symbols-outlined">edit</span>'
        badge_label = 'User-authored animation'
      } else if (source_skeleton_origin === 'mixamo') {
        badge_text = 'M'
        badge_label = 'Imported from Mixamo'
      } else if (source_skeleton_origin === 'quaternius') {
        badge_text = 'Q'
        badge_label = 'Imported from Quaternius / universal humanoid'
      } else if (source_skeleton_origin === 'tripo') {
        badge_text = 'T'
        badge_label = 'Imported from Tripo3D'
      } else if (is_custom_animation) {
        badge_text = 'C'
        badge_label = 'Custom animation'
      }
      const custom_animation_badge_html = badge_text === ''
        ? ''
        : `<span class="anim-custom-badge" title="${this.escape_html(badge_label)}" aria-label="${this.escape_html(badge_label)}">${badge_text}</span>`

      // Surface broken keyframes (NaN/Infinity values, empty tracks, bad
      // duration). Rendered as a small ⚠ badge in the top-right of the clip
      // thumb so the user notices before exporting.
      const validation = ClipValidator.validate(animation_clip)
      const validation_badge_html = ClipValidator.has_issues(validation)
        ? `<span class="anim-warning-badge" title="${this.escape_html(ClipValidator.summarize(validation))}" aria-label="${this.escape_html(ClipValidator.summarize(validation))}" style="position: absolute; top: 4px; right: 4px; background: #d4453a; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; pointer-events: auto;">!</span>`
        : ''

      const clip_actions_html = is_user_authored
        ? `
            <div class="user-clip-actions" aria-label="User clip actions">
              <button type="button" class="user-clip-action-button" data-clip-action="rename" data-index="${original_index}" title="Rename clip" aria-label="Rename clip">
                <span class="material-symbols-outlined">edit</span>
              </button>
              <button type="button" class="user-clip-action-button" data-clip-action="duplicate" data-index="${original_index}" title="Duplicate clip" aria-label="Duplicate clip">
                <span class="material-symbols-outlined">content_copy</span>
              </button>
              <button type="button" class="user-clip-action-button" data-clip-action="trim" data-index="${original_index}" title="Trim this clip to a sub-range (in seconds). Boundary values are interpolated so the trimmed clip starts and ends in the right pose." aria-label="Trim clip">
                <span class="material-symbols-outlined">content_cut</span>
              </button>
              <button type="button" class="user-clip-action-button" data-clip-action="export" data-index="${original_index}" title="Export this clip as a GLB file" aria-label="Export clip as GLB">
                <span class="material-symbols-outlined">download</span>
              </button>
              <button type="button" class="user-clip-action-button danger" data-clip-action="delete" data-index="${original_index}" title="Delete clip" aria-label="Delete clip">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>`
        : ''

      // User-authored clips have a captured PNG thumbnail (from the live
      // viewport at bake time) — render it inside the placeholder so the user
      // can recognize each clip at a glance.
      const thumbnail_data_url = is_user_authored ? animation_clip.metadata?.thumbnail_data_url : undefined
      const placeholder_inner_html = thumbnail_data_url !== undefined && thumbnail_data_url !== ''
        ? `<img class="user-clip-thumbnail" src="${this.escape_html(thumbnail_data_url)}" alt="" />`
        : ''

      const item_class = (is_custom_animation || is_user_authored) ? 'anim-custom-item' : 'anim-item'
      const animation_entry_html = `
        <div class="${item_class}">
          <button class="secondary-button play" data-index="${original_index}" style="display: flex; flex-direction:column; position: relative;">
            ${custom_animation_badge_html}
            ${validation_badge_html}
            <div class="anim-preview-placeholder"${preview_data_src_attribute} style="pointer-events: none;">${placeholder_inner_html}</div>
            <label class="styled-checkbox">
              <input type="checkbox" name="${this.escape_html(animation_clip.name)}" value="${original_index}" ${checked_attribute}>
              <span class="anim-preview-label">${this.escape_html(this.animation_name_clean(animation_clip.name))}</span>
            </label>
          </button>
          ${clip_actions_html}
        </div>`

      // append the entire item HTML to the DOM element
      this.animation_list_container.innerHTML += animation_entry_html
    })

    // only so many WebM videos can be playing at the same time
    // so this is an optimization to convert only elements in the active scroll area to video elements
    this.setup_lazy_video_loading()
  }

  /**
   * Sets up lazy loading for video previews using Intersection Observer.
   * Only loads video elements when their placeholders are visible in the viewport.
   */
  private setup_lazy_video_loading (): void {
    // Only set up IntersectionObserver if the container exists
    // any animation entry that is in view will run this code to convert it to a video element
    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[], _obs: IntersectionObserver) => {
      entries.forEach(entry => {
        const placeholder = entry.target as HTMLElement

        // abort if animation entry is outside active viewing area (but don't unload - causes popping)
        if (!entry.isIntersecting) {
          return
        }

        // if element is already a video, and it is in view, don't convert
        // it to a video again, it is ok so abort any further work
        const existing_video = placeholder.querySelector('video')
        if (existing_video != null) {
          return
        }

        // element that just came into view and needs to be converted
        // to a video element
        const video = document.createElement('video')
        video.className = 'anim-preview'
        const src = placeholder.getAttribute('data-src') ?? ''
        video.src = src
        video.width = 100
        video.height = 120
        video.loop = true
        video.muted = true
        video.playsInline = true // tells mobile browsers to play inline instead of going fullscreen
        video.autoplay = true
        placeholder.innerHTML = ''
        placeholder.appendChild(video)
      })
    }, { rootMargin: '300px' }) // rootMargin pre-loads videos before they scroll into view to reduce popping

    // grabs all the animation list elements and tells the observer to start watching them for processing
    const placeholders = this.animation_list_container?.querySelectorAll('.anim-preview-placeholder')
    placeholders?.forEach(ph => { observer.observe(ph) })
  }

  public animation_name_clean (input: string): string {
    return input.replace(/_/g, ' ')
  }

  private escape_html (input: string): string {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  /**
   * Gets the list of filtered animations. Returns all animations if no filtering
   * @returns An array of selected animations.
   */
  public get_selected_animations (): AnimationWithState[] {
    return this.all_animations.filter(animation => animation.isChecked === true)
  }

  /**
   * Gets the list of animations that are checked to be exported
   * @returns An array of selected animation indices.
   */
  public get_selected_animation_indices (): number[] {
    return this.all_animations
      .map((animation, index) => (animation.isChecked === true) ? index : -1)
      .filter(index => index !== -1)
  }

  public clear_filter (): void {
    if (this.filter_input !== null) {
      this.filter_input.value = ''
      this.render_filtered_animations('')
    }
  }
}
